import React, { useState } from 'react';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { NoSSR } from '@/components';
import { quotaExceededModal } from '@/shared/features/ai-quiz-generation/components';
import { useAiTrialQuotaQuery } from '@/shared/features/ai-quiz-generation/hooks';
import { saveAiAutoTriggerData } from '@/shared/features/ai-quiz-generation/utils/aiAutoTriggerStorage';
import { setReferrer } from '@/shared/features/enter-referrer-tracking';
import { AI_PROMPT_MAX_LENGTH } from '../constants';
import { usePresetPrompts } from '../hooks';
import { type PresetTopic } from '../types';
import { GradientStrokeInput } from './GradientStrokeInput';
import { PresetChips } from './PresetChips';
export const AiPromptGenerator: React.FC = () => {
  const router = useRouter();
  const [prompt, setPrompt] = useState('');
  const {
    presets
  } = usePresetPrompts();
  const {
    isQuotaExhausted
  } = useAiTrialQuotaQuery();
  const {
    t
  } = useTranslation(['landing']);
  const guardQuota = () => {
    if (isQuotaExhausted) {
      quotaExceededModal.open();
      return true;
    }
    return false;
  };
  const handleSubmit = (prompt: string) => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt) return;
    if (guardQuota()) return;
    setReferrer({
      from: '/landing',
      location: 'home_ai_input'
    });
    logger.track('click_ai_prompt_submit', {
      location: 'landing'
    });
    saveAiAutoTriggerData({
      prompt: trimmedPrompt
    });
    void router.push('/build');
  };
  const handlePresetClick = (preset: PresetTopic) => {
    if (guardQuota()) return;
    setReferrer({
      from: '/landing',
      location: 'home_ai_preset'
    });
    logger.track('click_ai_preset', {
      location: 'landing',
      topic_id: preset.topicId
    });
    saveAiAutoTriggerData({
      prompt: preset.label,
      topicId: preset.topicId
    });
    void router.push('/build');
  };
  return <section className="px-[16px] py-2xl min-[751px]:px-[24px] min-[1025px]:px-[40px] min-[1025px]:py-3xl" data-sentry-component="AiPromptGenerator" data-sentry-source-file="AiPromptGenerator.tsx">
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center gap-lg min-[751px]:gap-2xl">
        {/* AI - title */}
        <div className="flex w-full flex-col items-center gap-[12px]">
          {/* Robot Image */}
          <div className="relative h-[70px] w-[70px]">
            <img src="/assets/ai_face.png" alt="AI Robot" width={70} height={70} />
          </div>

          {/* Title */}
          <h2 className="text-center text-display-sm-extrabold text-default">
            <Trans t={t} i18nKey="landing.ai.title" components={{
            strong: <strong className="text-primary" />
          }} data-sentry-element="Trans" data-sentry-source-file="AiPromptGenerator.tsx" />
          </h2>
        </div>

        {/* Gradient Input */}
        <div className="w-full max-w-[1080px]">
          <GradientStrokeInput value={prompt} onChange={setPrompt} onSubmit={() => handleSubmit(prompt)} placeholder={t('landing.ai.placeholder')} maxLength={AI_PROMPT_MAX_LENGTH} data-sentry-element="GradientStrokeInput" data-sentry-source-file="AiPromptGenerator.tsx" />
        </div>

        {/* Preset Chips */}
        <NoSSR data-sentry-element="NoSSR" data-sentry-source-file="AiPromptGenerator.tsx">
          {presets.length > 0 && <div className="w-full max-w-[1080px]">
              <PresetChips presets={presets} onPresetClick={handlePresetClick} />
            </div>}
        </NoSSR>
      </div>
    </section>;
};